/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { UpdateChangeDtoStatusEnum } from '@codefluegel/zeta-change-typescript-client';
import { Autocomplete, Chip, SxProps, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';

/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import { TaskCardLocalizationList } from '../../constants/changes';

const enumToArray = (enumme: { [key: string]: string }) =>
  Object.keys(enumme).map(key => enumme[key]);

type ChangeFilterProps = {
  filteredStatuses: string[];
  setFilteredStatuses: (statuses: string[]) => void;
  possibleStatusList?: string[] | undefined;
  sx?: SxProps;
};

const ChangeFilter = observer(
  ({
    filteredStatuses,
    setFilteredStatuses,
    possibleStatusList,
    sx,
  }: ChangeFilterProps) => {
    const { t } = useTranslation();
    const TaskCardLocalization = TaskCardLocalizationList();

    const allStatuses =
      possibleStatusList || enumToArray(UpdateChangeDtoStatusEnum);

    const statuses = allStatuses.filter(
      status =>
        status !== UpdateChangeDtoStatusEnum.InternalApprovalRejected &&
        status !== UpdateChangeDtoStatusEnum.EvalCalcRejected,
    );

    const options = statuses.map(status => ({
      value: status,
      label: TaskCardLocalization[status as UpdateChangeDtoStatusEnum],
    }));

    const selectedOptions = options.filter(option =>
      filteredStatuses.includes(option.value),
    );

    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedOptions}
        onChange={(event, newValue) => {
          setFilteredStatuses(newValue.map(option => option.value));
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={t('status.status')}
            sx={{ background: 'white' }}
            placeholder={t('change.selectChangeStatus')}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              label={option.label}
              color="primary"
              size="small"
            />
          ))
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        sx={{ minWidth: 250, ...sx }}
      />
    );
  },
);

export default ChangeFilter;
